import { Button, useAlert } from '@ads/components-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCreateParcel } from '@/features/individual-payment/hooks/useCreateParcel';
import type { ParcelCreateFormValues } from '@/features/individual-payment/hooks/useParcelCreateFormSchema';
import { useParcelCreateFormSchema } from '@/features/individual-payment/hooks/useParcelCreateFormSchema';
import { useParcelCreateLookups } from '@/features/individual-payment/hooks/useParcelCreateLookups';
import { useParcelEnums } from '@/features/individual-payment/hooks/useParcelEnums';
import { ParcelCommunicationSection } from '@/features/individual-payment/ui/organisms/ParcelCommunicationSection';
import { ParcelContractCard } from '@/features/individual-payment/ui/organisms/ParcelContractCard';
import { ParcelIdentificationSection } from '@/features/individual-payment/ui/organisms/ParcelIdentificationSection';
import { ParcelRecurrenceBlock } from '@/features/individual-payment/ui/organisms/ParcelRecurrenceBlock';
import { ParcelValuesSection } from '@/features/individual-payment/ui/organisms/ParcelValuesSection';
import { ParcelCreateTemplate } from '@/features/individual-payment/ui/templates/ParcelCreateTemplate';
import { buildParcelCreatePayload } from '@/features/individual-payment/utils/buildParcelCreatePayload';
import { EMPTY_VALUE } from '@/features/individual-payment/utils/formatCurrency';
import type { PlanWriteError } from '@/features/individual-payment/utils/planWriteError';
import { Alert } from '@/shared/ui/atoms';

export const Route = createFileRoute(
  '/_authenticated/individual-payment/$financialPersonId/parcels/create/',
)({
  component: ParcelCreatePage,
});

/**
 * Página de criação de parcela manual em um contrato financeiro.
 *
 * Orquestra template, card de contrato e as quatro seções do formulário. O envio
 * fica bloqueado enquanto `typeId` não tiver valor — agora um bloqueio de
 * preenchimento, não de contrato: `shared/parcel-enums` passou a listar os tipos,
 * e o campo virou select de escrita. Antes a lista não existia em endpoint nenhum,
 * e a tela era inescapável.
 */
export function ParcelCreatePage() {
  const { t } = useTranslation('individualPayment');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { financialPersonId: rawId } = Route.useParams();

  // O parâmetro de rota é string. Converter aqui, na fronteira, evita propagar
  // `NaN` para a URL das requisições de apoio.
  const financialPersonId = useMemo(() => {
    const parsed = Number(rawId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [rawId]);

  const {
    header,
    isHeaderLoading,
    isHeaderError,
    refetchHeader,
    paymentMethods,
    arePaymentMethodsLoading,
    hasNoPaymentMethod,
    planPaymentTypeLabel,
  } = useParcelCreateLookups(financialPersonId);
  const { types, isLoading: areTypesLoading } = useParcelEnums(financialPersonId !== null);

  const { schema, defaultValues } = useParcelCreateFormSchema();
  const form = useForm<ParcelCreateFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues as ParcelCreateFormValues,
  });

  // A recusa de regra de negócio vive na página, não no formulário: ela não
  // pertence a um campo específico e precisa sobreviver à revalidação.
  const [writeError, setWriteError] = useState<PlanWriteError | null>(null);

  const { create, isPending } = useCreateParcel(financialPersonId);

  const backToParcels = () =>
    void navigate({
      to: '/individual-payment/$financialPersonId/parcels',
      params: { financialPersonId: rawId },
    });

  const handleSave = form.handleSubmit(values => {
    setWriteError(null);

    create(buildParcelCreatePayload(values), {
      onSuccess: () => {
        const requested = typeof values.amountOfParcel === 'number' ? values.amountOfParcel : 0;
        const amount = values.multiple === true ? requested : 1;
        alert({
          alertType: 'success',
          title:
            amount > 1
              ? t('parcelCreate.createSuccessMultiple', { count: amount })
              : t('parcelCreate.createSuccess'),
        });
        backToParcels();
      },
      onError: failure => setWriteError(failure),
    });
  });

  if (financialPersonId === null) {
    return (
      <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
        <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
          <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
        </span>
        <p className="ts-heading-xs text-ctx-content-title">{t('parcels.invalidId')}</p>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">{t('parcels.invalidIdHint')}</p>
        <Button
          hierarchy="secondary"
          size="md"
          onClick={() => void navigate({ to: '/individual-payment' })}
        >
          {t('parcels.backToList')}
        </Button>
      </div>
    );
  }

  if (isHeaderError) {
    return (
      <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
        <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
          <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
        </span>
        <p className="ts-heading-xs text-ctx-content-title">{t('parcels.headerError')}</p>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('parcels.table.errorHint')}
        </p>
        <Button hierarchy="secondary" size="md" onClick={refetchHeader}>
          {t('parcels.table.retry')}
        </Button>
      </div>
    );
  }

  /**
   * O tipo da parcela é obrigatório, então o envio espera a escolha.
   *
   * Sempre foi uma leitura do formulário, e é por isso que destravou sozinho: com
   * `shared/parcel-enums` alimentando o select, o campo passou a receber valor e o
   * botão liberou sem outra mudança aqui.
   */
  const canSave = form.watch('typeId') != null;

  return (
    <ParcelCreateTemplate
      financialPersonId={rawId}
      studentName={header?.studentName ?? EMPTY_VALUE}
      subtitle={t('parcelCreate.subtitle', { course: header?.courseName ?? EMPTY_VALUE })}
      isSaving={isPending}
      canSave={canSave}
      {...(canSave ? {} : { blockedReason: t('parcelCreate.validation.typeRequired') })}
      onSave={() => void handleSave()}
      onCancel={backToParcels}
    >
      {writeError !== null ? (
        <Alert
          type={writeError.kind === 'server' ? 'danger' : 'warning'}
          title={
            writeError.kind === 'server'
              ? t('parcelCreate.serverError')
              : writeError.kind === 'forbidden'
                ? t('parcelCreate.forbidden')
                : writeError.message === null
                  ? t('parcelCreate.businessErrorFallback')
                  : t('parcelCreate.businessError', { message: writeError.message })
          }
        />
      ) : null}

      <ParcelContractCard
        header={header}
        isLoading={isHeaderLoading}
        paymentTypeLabel={planPaymentTypeLabel}
        showReadOnlyBadge
      />

      <ParcelIdentificationSection
        form={form}
        paymentMethods={paymentMethods}
        arePaymentMethodsLoading={arePaymentMethodsLoading}
        hasNoPaymentMethod={hasNoPaymentMethod}
        types={types}
        areTypesLoading={areTypesLoading}
      />

      <ParcelValuesSection form={form} />

      <ParcelCommunicationSection form={form} />

      <ParcelRecurrenceBlock form={form} />
    </ParcelCreateTemplate>
  );
}
